import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FiArrowLeft, FiCheck, FiEye, FiEyeOff, FiX, FiLock,
  FiBarChart2, FiTarget, FiUsers, FiActivity, FiTrendingUp, FiCheckCircle,
} from "react-icons/fi";
import "./PmsLogin.css";
import { useRole } from "../../context/RoleContext";
import { acceptInvitation, validateInvitationLink, exchangeInvitationLink } from "../../services/invitationApi";
import useEmailLinkToken, { clearLinkToken, formatSeconds } from "../../hooks/useEmailLinkToken";
import { PASSWORD_MIN_LENGTH, PASSWORD_MAX_LENGTH, passwordPolicy } from "../../services/authApi";

function BrandMark() {
  return (
    <svg width="38" height="42" viewBox="0 0 38 42" aria-hidden="true" className="pmsx-logo-svg">
      <polygon points="19,1 36,10.5 36,31.5 19,41 2,31.5 2,10.5" fill="#ffffff" fillOpacity="0.14" stroke="#ffffff" strokeWidth="1.8" />
      <rect x="10" y="22" width="4" height="9" rx="1" fill="#ffffff" />
      <rect x="17" y="16" width="4" height="15" rx="1" fill="#ffffff" />
      <rect x="24" y="10" width="4" height="21" rx="1" fill="#4cc3ff" />
    </svg>
  );
}

function AuthNotice({ tone = "info", children }) {
  const icon = tone === "success" ? "✓" : tone === "error" ? "!" : "i";
  return (
    <div className={`pmsx-notice pmsx-notice--${tone}`} role={tone === "error" ? "alert" : "status"}>
      <span className="pmsx-notice-icon" aria-hidden="true">{icon}</span>
      <span>{children}</span>
    </div>
  );
}

function AuthShell({ title, subtitle, label, children }) {
  return (
    <main className="pmsx-root" aria-label={label}>
      <div className="pmsx-decor" aria-hidden="true">
        <span className="pmsx-hex pmsx-hex-1"><FiBarChart2 size={22} /></span>
        <span className="pmsx-hex pmsx-hex-2"><FiTarget size={20} /></span>
        <span className="pmsx-hex pmsx-hex-3"><FiUsers size={20} /></span>
        <span className="pmsx-hex pmsx-hex-4"><FiActivity size={22} /></span>
        <span className="pmsx-hex pmsx-hex-5"><FiTrendingUp size={20} /></span>
        <span className="pmsx-hex pmsx-hex-6"><FiCheckCircle size={18} /></span>
      </div>

      <section className="pmsx-panel">
        <div className="pmsx-brand">
          <BrandMark />
          <span className="pmsx-brand-name">TELIOLABS</span>
        </div>

        <div className="pmsx-heading">
          <h1 className="pmsx-title">{title}</h1>
          {subtitle ? <p className="pmsx-subtitle">{subtitle}</p> : null}
        </div>

        {children}
      </section>

      <footer className="pmsx-footer">
        <p>Not able to login? Contact HR Team</p>
        <a href="mailto:HR@teliolabs.com">HR@teliolabs.com</a>
        <p className="pmsx-copyright">&copy; 2026 Teliolabs. All rights reserved.</p>
      </footer>
    </main>
  );
}

function PasswordInput({ id, label, value, onChange, disabled, autoFocus, onEnter }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="pmsx-field">
      <label className="pmsx-sr-only" htmlFor={id}>{label}</label>
      <FiLock className="pmsx-field-icon" size={17} aria-hidden="true" />
      <input
        id={id}
        type={visible ? "text" : "password"}
        autoComplete="new-password"
        placeholder={label}
        value={value}
        maxLength={PASSWORD_MAX_LENGTH}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => { if (event.key === "Enter" && onEnter) onEnter(event); }}
        disabled={disabled}
        autoFocus={autoFocus}
        className="pmsx-has-toggle"
      />
      <button
        type="button"
        className="pmsx-password-toggle"
        onClick={() => setVisible((v) => !v)}
        disabled={disabled}
        aria-label={visible ? "Hide password" : "Show password"}
        aria-pressed={visible}
        tabIndex={-1}
      >
        {visible ? <FiEye size={17} /> : <FiEyeOff size={17} />}
      </button>
    </div>
  );
}

function Rule({ ok, children }) {
  return (
    <li className={ok ? "is-ok" : ""}>
      {ok ? <FiCheck size={12} aria-hidden="true" /> : <FiX size={12} aria-hidden="true" />}
      <span>{children}</span>
      <span className="pmsx-sr-only">{ok ? " (done)" : " (required)"}</span>
    </li>
  );
}

export default function AcceptInvitationPage() {
  // [UPDATED] Validate + exchange the e-mail token first (new backend endpoints
  // GET /auth/accept-invitation/validate and POST /auth/accept-invitation/exchange).
  const link = useEmailLinkToken("invite", {
    validate: validateInvitationLink,
    exchange: exchangeInvitationLink,
    invalidCodes: ["INVITATION_INVALID"],
  });
  const token = link.token;
  const { isAuthenticated, logout } = useRole();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitState, setStatus] = useState("idle");
  const linkExpired = link.status === "ready" && link.secondsLeft === 0;
  const status = link.status === "checking" ? "checking"
    : link.status === "invalid" || linkExpired ? (submitState === "done" ? "done" : "invalid")
    : link.status === "error" ? "linkError"
    : submitState;
  const [username, setUsername] = useState("");
  const [error, setError] = useState("");

  const rules = useMemo(() => ({
    ...passwordPolicy(password),
    match: password.length > 0 && password === confirm,
  }), [password, confirm]);

  const submit = async (event) => {
    event?.preventDefault?.();
    if (!rules.length || !rules.lowercase || !rules.uppercase || !rules.digit || !rules.special || !rules.noSpaces) { setError("Use 8 to 128 characters with uppercase, lowercase, a digit, and a special character; spaces are not allowed."); return; }
    if (!rules.match) { setError("The two passwords don't match."); return; }
    setError("");
    setStatus("saving");
    try {
      const { data } = await acceptInvitation({ token, newPassword: password });
      clearLinkToken("invite");
      if (isAuthenticated) logout({ local: true });
      setUsername(data?.username || "");
      setPassword("");
      setConfirm("");
      setStatus("done");
    } catch (err) {
      const code = err?.response?.data?.error?.code;
      if (code === "INVITATION_INVALID") { clearLinkToken("invite"); setStatus("invalid"); return; }
      setStatus("idle");
      if (!err?.response) { setError("Unable to reach the server. Check your connection and try again."); return; }
      if (err.response.status === 429) { setError("Too many attempts. Please wait a few minutes and try again."); return; }
      if (err.response.status === 422) { setError("Password does not meet the required complexity rules."); return; }
      setError(err.response.data?.error?.message || "Could not set the password. Please try again.");
    }
  };

  if (status === "done") {
    return (
      <AuthShell title="You're all set" label="Teliolabs PMS invitation accepted">
        <div className="pmsx-form">
          <AuthNotice tone="success">
            Your password is saved{username ? <>. Sign in as <strong>{username}</strong></> : ""}.
          </AuthNotice>
          <Link to="/login" className="pmsx-login-btn pmsx-link-btn">Go to login</Link>
        </div>
      </AuthShell>
    );
  }

  if (status === "invalid") {
    return (
      <AuthShell title="Link not valid" label="Teliolabs PMS invitation link not valid">
        <div className="pmsx-form">
          <AuthNotice tone="error">
            This invitation link is invalid, already used, or has expired. Ask HR to send you a new invitation.
          </AuthNotice>
          <Link to="/login" className="pmsx-back-link">
            <FiArrowLeft size={15} aria-hidden="true" />
            <span>Back to login</span>
          </Link>
        </div>
      </AuthShell>
    );
  }

  // [NEW] Shown while the link is being validated and exchanged.
  if (status === "checking") {
    return (
      <AuthShell title="Checking your invitation" label="Teliolabs PMS checking invitation">
        <div className="pmsx-loading">
          <span className="pmsx-spinner" />
          <span>Checking your invitation link...</span>
        </div>
      </AuthShell>
    );
  }

  // [NEW] Server could not be reached while checking the link.
  if (status === "linkError") {
    return (
      <AuthShell title="Welcome to Teliolabs PMS" label="Teliolabs PMS accept invitation">
        <div className="pmsx-form">
          <AuthNotice tone="error">{link.message || "We could not check your invitation link. Please try again."}</AuthNotice>
          <button type="button" className="pmsx-login-btn" onClick={link.retry}>Try again</button>
        </div>
      </AuthShell>
    );
  }

  const busy = status === "saving";
  return (
    <AuthShell title="Welcome to Teliolabs PMS" subtitle="Choose the password for your new login." label="Teliolabs PMS accept invitation">
      <form className="pmsx-form" onSubmit={submit} noValidate>
        {error ? <AuthNotice tone="error">{error}</AuthNotice> : null}
        {link.secondsLeft != null ? (
          <AuthNotice tone="info">
            Please set your password within {formatSeconds(link.secondsLeft)}. For your security the e-mail link has now been used; if time runs out, ask HR to resend the invitation.
          </AuthNotice>
        ) : null}
        <PasswordInput id="pmsx-invite-new" label="New password" value={password} onChange={setPassword} disabled={busy} autoFocus />
        <PasswordInput id="pmsx-invite-confirm" label="Confirm password" value={confirm} onChange={setConfirm} disabled={busy} onEnter={submit} />
        <ul className="pmsx-rules" aria-label="Password rules">
          <Rule ok={rules.length}>{PASSWORD_MIN_LENGTH}-{PASSWORD_MAX_LENGTH} characters</Rule>
          <Rule ok={rules.lowercase}>Lowercase</Rule>
          <Rule ok={rules.uppercase}>Uppercase</Rule>
          <Rule ok={rules.digit}>Digit</Rule>
          <Rule ok={rules.special}>Special character</Rule>
          <Rule ok={rules.noSpaces}>No spaces</Rule>
          <Rule ok={rules.match}>Passwords match</Rule>
        </ul>
        <button type="submit" className="pmsx-login-btn" disabled={!(rules.length && rules.lowercase && rules.uppercase && rules.digit && rules.special && rules.noSpaces && rules.match) || busy}>
          {busy ? (
            <span className="pmsx-btn-loading"><span className="pmsx-spinner" /><span>Saving...</span></span>
          ) : "Set password"}
        </button>
      </form>
    </AuthShell>
  );
}